import {
  MailIcon,
  TagIcon,
  SparklesIcon,
  HeartIcon,
  IdentificationIcon,
  EyeIcon,
} from '@heroicons/react/outline';
import type { PopularRecipe } from './popularRecipes';
import type { AutomationRulePartial } from './types';
import Button from '../../components/Button';
import { getWorkflowTriggerLabel, getWorkflowActionLabel } from './RuleList';

type PopularRecipeCardProps = {
  recipe: PopularRecipe;
  onUseRecipe: (recipe: PopularRecipe) => void;
};

const RECIPE_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  'tag-new-client': TagIcon,
  'welcome-new-clients': HeartIcon,
  'first-session-reminder': MailIcon,
  'crisis-support-follow-up': MailIcon,
  'send-intake-package': IdentificationIcon,
  'send-preparation': EyeIcon,
};

const RECIPE_ACCENT_CLASSES: Record<
  string,
  { bg: string; border: string; icon: string }
> = {
  'tag-new-client': {
    bg: 'bg-primary-25',
    border: 'border-primary-100',
    icon: 'text-primary-125',
  },
  'welcome-new-clients': {
    bg: 'bg-primary-25',
    border: 'border-primary-100',
    icon: 'text-primary-125',
  },
  'crisis-support-follow-up': {
    bg: 'bg-primary-25',
    border: 'border-primary-100',
    icon: 'text-primary-125',
  },
  'first-session-reminder': {
    bg: 'bg-primary-25',
    border: 'border-primary-100',
    icon: 'text-primary-125',
  },
  'send-intake-package': {
    bg: 'bg-primary-25',
    border: 'border-primary-100',
    icon: 'text-purple-125',
  },
  'send-preparation': {
    bg: 'bg-primary-25',
    border: 'border-primary-100',
    icon: 'text-primary-125',
  },
};

export default function PopularRecipeCard({
  recipe,
  onUseRecipe,
}: PopularRecipeCardProps) {
  const Icon = RECIPE_ICONS[recipe.id] ?? MailIcon;
  const accent = RECIPE_ACCENT_CLASSES[recipe.id] ?? {
    bg: 'bg-neutral-25',
    border: 'border-neutral-200',
    icon: 'text-neutral-120',
  };
  const ruleLike: AutomationRulePartial = {
    name: recipe.title,
    triggerEvent: recipe.triggerEvent,
    actions: recipe.actions.map((a) => ({
      type: a.type,
      params: a.params
        ? Object.entries(a.params).map(([key, value]) => ({ key, value }))
        : [],
    })),
  };
  const triggerDisplay = getWorkflowTriggerLabel(ruleLike);
  const actionDisplay = getWorkflowActionLabel(ruleLike);
  const TriggerIcon = triggerDisplay.icon;
  const ActionIcon = actionDisplay.icon;

  return (
    <div
      className={`flex flex-col rounded-lg border bg-white p-4 shadow-100 ${accent.border}`}
      data-testid="popular-recipe-card"
    >
      <div className="flex-1">
        <div className="mb-3 flex items-center gap-2">
          <Icon className={`h-5 w-5 shrink-0 ${accent.icon}`} aria-hidden />
          <h3 className="min-w-0 font-sans text-body font-semibold text-neutral-150">
            {recipe.title}
          </h3>
        </div>
        <p className="text-xs text-neutral-110">{recipe.description}</p>
      </div>
      <div className="mt-4 rounded-md bg-neutral-50 p-2.5">
        <div className="flex flex-col items-start gap-1.5">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-100 bg-primary-25 px-2 py-1 text-[11px] text-neutral-150">
              <TriggerIcon className="h-3.5 w-3.5 shrink-0" />
              {triggerDisplay.label}
            </span>
            <span className="text-neutral-120 text-[11px]">→</span>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-green-100 bg-green-25 px-2 py-1 text-[11px] text-neutral-150">
            <ActionIcon className="h-3.5 w-3.5 shrink-0 text-green-125" />
            {actionDisplay.label}
          </span>
        </div>
      </div>
      <Button
        theme="primary"
        size="small"
        title="Use Recipe"
        IconComponent={SparklesIcon}
        iconPosition="left"
        onClick={() => onUseRecipe(recipe)}
        className="mt-4 self-start"
      />
    </div>
  );
}
